/**
 * corpAuth.ts
 *
 * Central contract for handing off to the corporate auth app
 * (corp-prospera-auth, e.g. https://login.prospera.ai).
 *
 * The direct username/password login on this app is being deprecated in favor
 * of logging in through the corporate app. `/login` auto-redirects to the URL
 * built here; the corp app authenticates the user and returns to
 * `/auth/callback?code=…&next=…`, which CorporateAuthCallbackPage exchanges for
 * a v5 session.
 */

/** Base origin of the corp auth app, or '' when unconfigured (dev). */
export function getCorpAuthBaseUrl(): string {
  return import.meta.env.VITE_CORP_AUTH_URL?.trim() ?? '';
}

interface CorporateLoginUrlOptions {
  /** In-app path to land on after auth completes (embedded in redirect_uri). */
  next?: string;
  /**
   * Force the corp account-list page instead of letting a single-account,
   * non-admin user be auto-logged-in. Sent as `prompt=select_account`, which
   * corp's AccountSelector honors. Use on deliberate logout so a user isn't
   * immediately signed back into their only account.
   */
  forceAccountSelection?: boolean;
}

/**
 * Build the corporate login URL, or null when VITE_CORP_AUTH_URL is unset so
 * callers can fall back to the local form (dev / misconfiguration safety).
 */
export function buildCorporateLoginUrl(opts: CorporateLoginUrlOptions = {}): string | null {
  const base = getCorpAuthBaseUrl();
  if (!base) return null;

  const next = opts.next ?? '/';
  // Preserve the existing contract: `next` is embedded inside redirect_uri.
  const redirectUri = `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`;
  const qs = new URLSearchParams({ redirect_uri: redirectUri });
  if (opts.forceAccountSelection) {
    qs.set('prompt', 'select_account');
  }
  return `${base.replace(/\/$/, '')}/?${qs.toString()}`;
}

/**
 * Redirect the browser to the corp app for a deliberate logout, forcing the
 * account-list page. Falls back to the local `/login` route (which renders the
 * form) when the corp app isn't configured.
 */
export function redirectToCorpLogout(): void {
  window.location.replace(buildCorporateLoginUrl({ forceAccountSelection: true }) ?? '/login');
}
